import { useMemo, useState, type ChangeEvent, type FormEvent, type ReactElement } from 'react';
import type { FleetAircraftView, AircraftType, NewFlightLeg } from '../../../domain/models/contracts';
import {
  MINUTES_PER_DAY,
  estimateBlockMinutes,
  formatDuration,
  formatMinuteOfDay,
  haversineKm,
  hasErrors,
  legsForAircraft,
  listDestinationCandidates,
  locationAt,
  mod,
  parseTimeInput,
  validateNewLeg,
  type ConstraintIssue,
  type ScheduleContext,
} from '../../../domain/scheduling/scheduleMath';
import { IssueList } from './IssueList';
import { ModalShell } from './ModalShell';

interface AddFlightLegModalProps {
  aircraft: FleetAircraftView;
  type: AircraftType;
  context: ScheduleContext;
  initialDepartureMinute: number;
  /** Pre-selects the destination, e.g. when the route planner hands over a route. */
  initialDestinationId?: string | null;
  onSubmit: (leg: NewFlightLeg) => void;
  onClose: () => void;
}

interface Evaluation {
  draft: NewFlightLeg;
  issues: ConstraintIssue[];
}

const CANDIDATE_LIMIT = 60;

export function AddFlightLegModal({
  aircraft,
  type,
  context,
  initialDepartureMinute,
  initialDestinationId,
  onSubmit,
  onClose,
}: AddFlightLegModalProps): ReactElement {
  const [departureMinute, setDepartureMinute] = useState<number>(initialDepartureMinute);
  const [query, setQuery] = useState<string>('');
  const [destinationId, setDestinationId] = useState<string | null>(initialDestinationId ?? null);

  const { airports, schedule } = context;

  const aircraftLegs = useMemo(
    () => legsForAircraft(schedule, aircraft.registration),
    [schedule, aircraft.registration],
  );

  // The aircraft can only depart from wherever it will be sitting at that time.
  const originId = locationAt(aircraft, aircraftLegs, departureMinute);
  const origin = airports[originId];
  const destination = destinationId === null ? undefined : airports[destinationId];

  const candidates = useMemo(
    () =>
      origin === undefined
        ? []
        : listDestinationCandidates(origin, type, airports, query, CANDIDATE_LIMIT),
    [origin, type, airports, query],
  );

  const blockMinutes =
    origin !== undefined && destination !== undefined
      ? estimateBlockMinutes(haversineKm(origin, destination), type.cruiseSpeedKmh)
      : 0;

  const evaluation = useMemo<Evaluation | null>(() => {
    if (destination === undefined) return null;
    const draft: NewFlightLeg = {
      aircraftReg: aircraft.registration,
      origin: originId,
      destination: destination.id,
      departureMinute,
      blockMinutes,
    };
    return { draft, issues: validateNewLeg(context, draft) };
  }, [context, destination, originId, departureMinute, blockMinutes, aircraft.registration]);

  const onTimeChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const parsed = parseTimeInput(event.target.value);
    if (parsed !== null) setDepartureMinute(parsed);
  };

  const canSubmit = evaluation !== null && !hasErrors(evaluation.issues);

  const onFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (evaluation !== null && canSubmit) onSubmit(evaluation.draft);
  };

  const arrival = mod(departureMinute + blockMinutes, MINUTES_PER_DAY);
  const readyAgain = mod(departureMinute + blockMinutes + type.minTurnaroundMinutes, MINUTES_PER_DAY);

  return (
    <ModalShell
      title="Add Flight Leg"
      subtitle={`${aircraft.registration} · ${type.id} · ${Math.round(type.maxRangeKm).toLocaleString()} km range · ${Math.round(type.minRunwayRequiredMeters).toLocaleString()} m runway`}
      onClose={onClose}
    >
      <form className="fsg-modal-body" onSubmit={onFormSubmit}>
        <div className="fsg-field-row">
          <label className="fsg-field">
            <span className="fsg-field-label">Departure (sim UTC)</span>
            <input
              className="fsg-input"
              type="time"
              step={300}
              value={formatMinuteOfDay(departureMinute)}
              onChange={onTimeChange}
            />
          </label>
          <div className="fsg-field">
            <span className="fsg-field-label">Origin (aircraft position)</span>
            <div className="fsg-static">
              {origin === undefined ? originId : `${origin.id} · ${origin.name}`}
            </div>
          </div>
        </div>

        <label className="fsg-field">
          <span className="fsg-field-label">Destination (in range, runway OK)</span>
          <input
            className="fsg-input"
            type="search"
            placeholder="Search by code or name"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
        </label>

        <div className="fsg-listbox" role="listbox" aria-label="Destination airports">
          {origin === undefined && (
            <p className="fsg-empty">Origin airport {originId} is not in the airport database.</p>
          )}
          {origin !== undefined && candidates.length === 0 && (
            <p className="fsg-empty">No airports within range and runway limits match.</p>
          )}
          {candidates.map((candidate) => (
            <button
              key={candidate.airport.id}
              type="button"
              role="option"
              aria-selected={candidate.airport.id === destinationId}
              className={
                candidate.airport.id === destinationId ? 'fsg-option is-selected' : 'fsg-option'
              }
              onClick={() => setDestinationId(candidate.airport.id)}
            >
              <span className="fsg-option-code">{candidate.airport.id}</span>
              <span className="fsg-option-name">{candidate.airport.name}</span>
              <span className="fsg-option-meta">
                {Math.round(candidate.distanceKm).toLocaleString()} km ·{' '}
                {formatDuration(candidate.blockMinutes)}
              </span>
            </button>
          ))}
        </div>

        {destination !== undefined && (
          <dl className="fsg-summary">
            <div>
              <dt>Route</dt>
              <dd>
                {originId} → {destination.id}
              </dd>
            </div>
            <div>
              <dt>Block time</dt>
              <dd>{formatDuration(blockMinutes)}</dd>
            </div>
            <div>
              <dt>Arrives</dt>
              <dd>{formatMinuteOfDay(arrival)}</dd>
            </div>
            <div>
              <dt>Ready again</dt>
              <dd>
                {formatMinuteOfDay(readyAgain)} ({type.minTurnaroundMinutes} min TAT)
              </dd>
            </div>
          </dl>
        )}

        {evaluation !== null && <IssueList issues={evaluation.issues} />}
        {destination === undefined && (
          <p className="fsg-hint">Choose a destination to validate the leg.</p>
        )}

        <footer className="fsg-modal-foot">
          <button type="button" className="fsg-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="fsg-btn fsg-btn-primary" disabled={!canSubmit}>
            Add Flight Leg
          </button>
        </footer>
      </form>
    </ModalShell>
  );
}
